const apiUrl = process.env.REACT_APP_API_URL;

export interface GameLogEntry {
    id: number;
    id_usuari: number;
    moment: string;
    tipo: string;
}

export interface UserGamePosition {
    pos_x: number;
    y?: number;
    pos_y: number;
    scene: string;
    id_actividad: number | null;
    salut: number | null;
}

export interface UserOnlineGameStatus {
    online: boolean;
    roomId?: string;
    x?: number;
    y?: number;
    username?: string;
    state?: any;
}

export interface UserStatsUnity {
    pj?: string;
    escut?: string;
    arc?: string;
    arma?: string;
    flecha?: string;
    magia?: string;
    skinType?: string;
    velocitat?: number;
    salut?: number;
    skinDefensa?: number;
    armaDany?: number;
    armaCritico?: number;
    armaVelocidad?: number;
    escutDefensa?: number;
    escutBloqueo?: number;
    escutRegeneracion?: number;
    arcDany?: number;
    arcRang?: number;
    arcVelocidad?: number;
    flechaDany?: number;
    flechaCritico?: number;
    magiaDany?: number;
    magiaRang?: number;
    magiaTipo?: string;
    magiaElemento?: string;
    magiaEsEscudo?: string;
}

export interface UserEquipmentIds {
    id_skin?: number;
    id_arma?: number;
    id_escut?: number;
    id_arc?: number;
    id_avatar?: number;
    id_flecha?: number;
    id_magia?: number;
}

// 1. Obtener logs de sesiones del usuario con filtro de fechas
export const GetUserGameLogs = async (
    userId: string | number,
    dataInici?: string,
    dataFi?: string
): Promise<GameLogEntry[]> => {
    try {
        const params = new URLSearchParams();
        if (dataInici) params.append("dataInici", dataInici);
        if (dataFi) params.append("dataFi", dataFi);
        const query = params.toString() ? `?${params.toString()}` : "";
        const response = await fetch(`${apiUrl}:8061/usuaris/${userId}/game/logs${query}`);
        if (!response.ok) return [];
        return await response.json();
    } catch (e) {
        console.error("Error al obtener logs de juego:", e);
        return [];
    }
};

// 2. Obtener estado en tiempo real de si está online en Unity
export const GetUserOnlineGameStatus = async (
    userId: string | number
): Promise<UserOnlineGameStatus> => {
    try {
        const response = await fetch(`${apiUrl}:8061/game/user/${userId}/online-status`);
        if (!response.ok) return { online: false };
        return await response.json();
    } catch (e) {
        console.error("Error al obtener estado online de juego:", e);
        return { online: false };
    }
};

// 3. Obtener todos los usuarios online en Unity
export const GetAllGameOnlineUsers = async (): Promise<Record<string, any>> => {
    try {
        const response = await fetch(`${apiUrl}:8061/game/online-users`);
        if (!response.ok) return {};
        return await response.json();
    } catch (e) {
        console.error("Error al obtener usuarios online de juego:", e);
        return {};
    }
};

// 4. Obtener posición, escena y salud guardada
export const GetUserGamePosition = async (
    userId: string | number
): Promise<UserGamePosition | null> => {
    try {
        const response = await fetch(`${apiUrl}:8061/usuaris/${userId}/position`);
        if (!response.ok) return null;
        const data = await response.json();
        if (Array.isArray(data) && data.length > 0) {
            return data[0];
        }
        return data;
    } catch (e) {
        console.error("Error al obtener posición de juego:", e);
        return null;
    }
};

// 5. Obtener stats y equipamiento completo de Unity
export const GetUserGameStatsUnity = async (
    userId: string | number
): Promise<UserStatsUnity | null> => {
    try {
        const response = await fetch(`${apiUrl}:8061/jutge/game/auto-login/${userId}`);
        if (!response.ok) return null;
        const data = await response.json();
        if (data && data.permisos && data.permisos.length > 0) {
            return data.permisos[0] as UserStatsUnity;
        }
        return null;
    } catch (e) {
        console.error("Error al obtener stats de Unity:", e);
        return null;
    }
};

// 6. Obtener interactables / cofres reclamados por el usuario
export const GetUserInteractableRewardsList = async (
    userId: string | number
): Promise<any[]> => {
    try {
        const response = await fetch(`${apiUrl}:8061/game/interactable/list?userId=${userId}`);
        if (!response.ok) return [];
        return await response.json();
    } catch (e) {
        console.error("Error al obtener cofres/interactables de usuario:", e);
        return [];
    }
};

// 7. Resetear posición del jugador a 0, 0
export const ResetUserGamePosition = async (
    userId: string | number
): Promise<boolean> => {
    try {
        const response = await fetch(`${apiUrl}:8061/usuaris/${userId}/position`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ x: 0, y: 0, tipo: 'RESET_ADMIN' })
        });
        return response.ok;
    } catch (e) {
        console.error("Error al resetear posición de juego:", e);
        return false;
    }
};
